import { useEffect, useState } from "react";
import { Loader2, Globe, Copy, Sparkles, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import Login from "./Login";
import {
  getCreatorSettings, updateCreatorSettings, publishAll, backfillTopics,
  setShowVisibility, listShows, rebrandAudio, ingestFolder, pullMomentsAll,
} from "../lib/api";

export default function PublicSettings() {
  const { user, loading } = useAuth();
  const [settings, setSettings] = useState(null);
  const [shows, setShows] = useState([]);
  const [handle, setHandle] = useState("");
  const [bio, setBio] = useState("");
  const [saving, setSaving] = useState(false);
  const [tagging, setTagging] = useState(false);
  const [featured, setFeatured] = useState("");
  const [destUrl, setDestUrl] = useState("");
  const [destLabel, setDestLabel] = useState("");
  const [folderUrl, setFolderUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [accentColor, setAccentColor] = useState("");
  const [discord, setDiscord] = useState("");
  const [socials, setSocials] = useState({});
  const [tipVenmo, setTipVenmo] = useState("");
  const [tipCashapp, setTipCashapp] = useState("");
  const [tipPaypal, setTipPaypal] = useState("");

  const load = () => {
    getCreatorSettings().then((s) => {
      setSettings(s); setHandle(s.handle || ""); setBio(s.bio || "");
      setFeatured((s.featured_topics || []).join(", "));
      setDestUrl(s.destination_url || ""); setDestLabel(s.destination_label || "");
      setAccentColor(s.accent_color || ""); setDiscord(s.discord_url || "");
      setSocials(s.socials || {});
      setTipVenmo(s.tip_venmo || ""); setTipCashapp(s.tip_cashapp || ""); setTipPaypal(s.tip_paypal || "");
    });
    listShows().then(setShows);
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (loading) return <div className="grid h-screen place-items-center bg-background text-accent"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!user) return <Login />;
  if (!settings) return <div className="grid h-screen place-items-center bg-background text-accent"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const publicUrl = settings.handle ? `${window.location.origin}/u/${settings.handle}` : null;

  const save = async (patch) => {
    setSaving(true);
    try {
      const s = await updateCreatorSettings(patch);
      setSettings((prev) => ({ ...prev, ...s }));
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const doPublishAll = async () => {
    const r = await publishAll();
    toast.success(`${r.public_shows} shows are now public`);
    load();
  };

  const doTagArchive = async () => {
    setTagging(true);
    toast.info("Tagging your archive by topic… this can take a minute.");
    try {
      const r = await backfillTopics();
      toast.success(`Tagged ${r.shows_tagged} shows by topic`);
      load();
    } catch { toast.error("Tagging failed"); } finally { setTagging(false); }
  };

  const toggleShow = async (s) => {
    const next = !s.is_public;
    await setShowVisibility(s.id, next);
    setShows((prev) => prev.map((x) => (x.id === s.id ? { ...x, is_public: next } : x)));
  };

  const doImportFolder = async () => {
    if (!folderUrl.includes("dropbox.com")) { toast.error("Paste a Dropbox folder share link"); return; }
    setImporting(true);
    try {
      await ingestFolder(folderUrl.trim());
      toast.success("Importing your Dropbox folder — each show is being downloaded, optimized and mined in the background.");
      setFolderUrl("");
      setTimeout(load, 4000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  return (
    <div className="min-h-screen bg-background text-white" data-testid="public-settings-page">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <a href="/" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-accent">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </a>
          <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-accent"><Globe className="h-4 w-4" /> Public Site</span>
        </div>
      </header>

      <div className="mx-auto max-w-4xl space-y-8 px-6 py-10">
        <div>
          <h1 className="font-display text-4xl tracking-wide">Your public site</h1>
          <p className="mt-2 font-body text-sm text-muted-foreground">A no-login page where fans browse, search and share your clips by topic — every share credits you.</p>
        </div>

        {/* Destination website — where this creator's content points */}
        <section className="border border-border bg-card p-6" data-testid="destination-section">
          <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Your destination website</label>
          <p className="mt-1 font-body text-xs text-muted-foreground">Point your work to your own site. A "Listen to every full show" button on your page will link here (e.g. barthandfriends.com). Leave blank to hide it.</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <input data-testid="destination-url-input" value={destUrl} onChange={(e) => setDestUrl(e.target.value)}
                   placeholder="barthandfriends.com"
                   className="rounded-none border border-border bg-background px-3 py-2 font-mono text-sm text-white outline-none focus:border-accent" />
            <input data-testid="destination-label-input" value={destLabel} onChange={(e) => setDestLabel(e.target.value)}
                   placeholder="Button label (e.g. barthandfriends.com)"
                   className="rounded-none border border-border bg-background px-3 py-2 font-mono text-sm text-white outline-none focus:border-accent" />
          </div>
          <button data-testid="save-destination" disabled={saving}
                  onClick={() => save({ destination_url: destUrl, destination_label: destLabel })}
                  className="mt-3 bg-accent px-4 py-2 font-body text-xs font-semibold text-black disabled:opacity-50">Save destination</button>
        </section>

        {/* Make it your own — accent color, socials, Discord, tip jar */}
        <section className="border border-border bg-card p-6" data-testid="brand-section">
          <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Make it your own</label>
          <p className="mt-1 font-body text-xs text-muted-foreground">Set your accent color, link your socials + Discord, and add a tip jar — all inside the Clip Miner template.</p>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="font-body text-sm">Accent color</span>
            <input type="color" data-testid="accent-color-input" value={accentColor || "#ccff00"} onChange={(e) => setAccentColor(e.target.value)}
                   className="h-9 w-14 cursor-pointer border border-border bg-background" />
            <input value={accentColor} onChange={(e) => setAccentColor(e.target.value)} placeholder="#E83F3F"
                   className="w-28 border border-border bg-background px-2 py-1.5 font-mono text-xs text-white outline-none focus:border-accent" />
            {accentColor && <button onClick={() => setAccentColor("")} className="font-mono text-[11px] text-muted-foreground hover:text-accent">reset to default</button>}
          </div>

          <p className="mt-5 font-mono text-xs uppercase tracking-widest text-muted-foreground">Social links</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {["instagram", "tiktok", "youtube", "x", "facebook", "website"].map((k) => (
              <input key={k} data-testid={`social-input-${k}`} value={socials[k] || ""} onChange={(e) => setSocials((p) => ({ ...p, [k]: e.target.value }))}
                     placeholder={`${k} link`} className="border border-border bg-background px-3 py-2 font-mono text-xs text-white outline-none focus:border-accent" />
            ))}
          </div>
          <input data-testid="discord-input" value={discord} onChange={(e) => setDiscord(e.target.value)} placeholder="Discord invite link (discord.gg/…)"
                 className="mt-2 w-full border border-border bg-background px-3 py-2 font-mono text-xs text-white outline-none focus:border-accent" />

          <p className="mt-5 font-mono text-xs uppercase tracking-widest text-muted-foreground">Tip jar</p>
          <p className="mt-1 font-body text-xs text-muted-foreground">Fans tap to tip via your own account — no fees, opens their app.</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            <input data-testid="tip-venmo-input" value={tipVenmo} onChange={(e) => setTipVenmo(e.target.value)} placeholder="Venmo username" className="border border-border bg-background px-3 py-2 font-mono text-xs text-white outline-none focus:border-accent" />
            <input data-testid="tip-cashapp-input" value={tipCashapp} onChange={(e) => setTipCashapp(e.target.value)} placeholder="Cash App $cashtag" className="border border-border bg-background px-3 py-2 font-mono text-xs text-white outline-none focus:border-accent" />
            <input data-testid="tip-paypal-input" value={tipPaypal} onChange={(e) => setTipPaypal(e.target.value)} placeholder="PayPal.me link or user" className="border border-border bg-background px-3 py-2 font-mono text-xs text-white outline-none focus:border-accent" />
          </div>

          <button data-testid="save-brand" disabled={saving}
                  onClick={() => save({ accent_color: accentColor, discord_url: discord, socials, tip_venmo: tipVenmo, tip_cashapp: tipCashapp, tip_paypal: tipPaypal })}
                  className="mt-4 bg-accent px-4 py-2 font-body text-xs font-semibold text-black disabled:opacity-50">Save branding</button>
        </section>

        {/* Featured subject tabs + tools */}
        <section className="border border-border bg-card p-6" data-testid="featured-topics-section">
          <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Featured subject tabs</label>
          <p className="mt-1 font-body text-xs text-muted-foreground">Comma-separated topics shown as big clickable tabs at the top of your page (e.g. addiction, divorce, domestic violence, faith). Leave blank to auto-list.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <input data-testid="featured-topics-input" value={featured} onChange={(e) => setFeatured(e.target.value)}
                   placeholder="addiction, divorce, domestic violence, faith, marriage"
                   className="min-w-[280px] flex-1 rounded-none border border-border bg-background px-3 py-2 font-mono text-sm text-white outline-none focus:border-accent" />
            <button data-testid="save-featured-topics" disabled={saving}
                    onClick={() => save({ featured_topics: featured.split(",").map((t) => t.trim()).filter(Boolean) })}
                    className="bg-accent px-4 py-2 font-body text-xs font-semibold text-black disabled:opacity-50">Save tabs</button>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-border pt-4">
            <a href="/manage" data-testid="manage-link" className="border border-border px-4 py-2 font-body text-xs hover:border-accent hover:text-accent">Manage / delete shows &amp; clips</a>
            <a href="/yana-admin" data-testid="yana-admin-link" className="border border-primary/50 px-4 py-2 font-body text-xs text-primary hover:bg-primary hover:text-white">Approve YANA friend signups</a>
            <button data-testid="rebrand-audio-btn"
                    onClick={async () => { await rebrandAudio(); toast.success("Re-branding your voice-recording clips (Gas Station Records) in the background."); }}
                    className="border border-border px-4 py-2 font-body text-xs hover:border-accent hover:text-accent">
              Re-brand voice clips with Gas Station Records
            </button>
            <button data-testid="pull-moments-btn"
                    onClick={async () => { await pullMomentsAll(); toast.success("Pulling Barth's stories & faith/Jesus moments across your archive (background)."); }}
                    className="border border-border px-4 py-2 font-body text-xs hover:border-accent hover:text-accent">
              Pull my stories &amp; faith moments
            </button>
          </div>
          <div className="mt-4 border-t border-border pt-4">
            <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Import full shows from a Dropbox folder</label>
            <p className="mt-1 font-body text-xs text-muted-foreground">Paste a Dropbox folder link. Every video/audio in it becomes a featured episode — auto-optimized, transcribed, mined for funny clips, and tagged with prayer/care + your stories &amp; faith moments (focused on you).</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <input data-testid="folder-url-input" value={folderUrl} onChange={(e) => setFolderUrl(e.target.value)}
                     placeholder="https://www.dropbox.com/scl/fo/…"
                     className="min-w-[280px] flex-1 rounded-none border border-border bg-background px-3 py-2 font-mono text-sm text-white outline-none focus:border-accent" />
              <button data-testid="import-folder-btn" disabled={importing} onClick={doImportFolder}
                      className="bg-accent px-4 py-2 font-body text-xs font-semibold text-black disabled:opacity-50">
                {importing ? "Importing…" : "Import folder"}
              </button>
            </div>
          </div>
        </section>

        {/* Handle + enable */}
        <section className="border border-border bg-card p-6">
          <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Your handle</label>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm text-muted-foreground">{window.location.origin}/u/</span>
            <input data-testid="handle-input" value={handle} onChange={(e) => setHandle(e.target.value)}
                   placeholder="yourname"
                   className="rounded-none border border-border bg-background px-3 py-2 font-mono text-sm text-white outline-none focus:border-accent" />
            <button data-testid="save-handle" disabled={saving} onClick={() => save({ handle, bio })}
                    className="bg-accent px-4 py-2 font-body text-xs font-semibold text-black disabled:opacity-50">Save</button>
          </div>
          <textarea data-testid="bio-input" value={bio} onChange={(e) => setBio(e.target.value)}
                    placeholder="Short bio for your public page…" rows={2}
                    className="mt-4 w-full rounded-none border border-border bg-background px-3 py-2 font-body text-sm text-white outline-none focus:border-accent" />

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
            <div>
              <p className="font-body text-sm">Public site {settings.public_enabled ? "live" : "off"}</p>
              <p className="font-mono text-xs text-muted-foreground">{settings.public_enabled ? "Visitors can view your page." : "Turn on to publish your page."}</p>
            </div>
            <button data-testid="toggle-public-enabled"
                    onClick={() => save({ public_enabled: !settings.public_enabled, handle: handle || undefined })}
                    className={`px-4 py-2 font-body text-xs font-semibold ${settings.public_enabled ? "border border-border text-white" : "bg-accent text-black"}`}>
              {settings.public_enabled ? "Turn off" : "Turn on"}
            </button>
          </div>

          {settings.public_enabled && publicUrl && (
            <div className="mt-4 flex items-center justify-between gap-3 border border-accent/30 bg-accent/[0.05] px-4 py-3">
              <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="public-url" className="truncate font-mono text-sm text-accent hover:underline">{publicUrl}</a>
              <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); }}
                      className="flex shrink-0 items-center gap-1.5 border border-border px-3 py-1.5 font-body text-xs hover:border-accent hover:text-accent">
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
            </div>
          )}
        </section>

        {/* Bulk actions */}
        <section className="grid gap-4 sm:grid-cols-2">
          <button data-testid="publish-all" onClick={doPublishAll}
                  className="flex items-center gap-3 border border-border bg-card p-5 text-left transition-colors hover:border-accent">
            <Globe className="h-6 w-6 text-accent" />
            <div>
              <p className="font-body text-sm font-semibold">Publish all shows</p>
              <p className="font-mono text-xs text-muted-foreground">Make your whole archive public in one click</p>
            </div>
          </button>
          <button data-testid="tag-archive" disabled={tagging} onClick={doTagArchive}
                  className="flex items-center gap-3 border border-border bg-card p-5 text-left transition-colors hover:border-accent disabled:opacity-60">
            {tagging ? <Loader2 className="h-6 w-6 animate-spin text-accent" /> : <Sparkles className="h-6 w-6 text-accent" />}
            <div>
              <p className="font-body text-sm font-semibold">Tag my archive by topic</p>
              <p className="font-mono text-xs text-muted-foreground">Auto-tag old clips so topic filters work</p>
            </div>
          </button>
        </section>

        {/* Per-show visibility */}
        <section>
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Shows ({shows.length})</p>
          <div className="mt-3 divide-y divide-border border border-border">
            {shows.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`show-row-${s.id}`}>
                <div className="min-w-0">
                  <p className="truncate font-body text-sm">{s.title}</p>
                  <p className="font-mono text-[11px] text-muted-foreground">
                    {s.status === "completed" ? `${s.clip_count} clips` : s.status}
                    {s.topics?.length > 0 && ` · ${s.topics.slice(0, 4).join(", ")}`}
                  </p>
                </div>
                <button data-testid={`toggle-show-${s.id}`} onClick={() => toggleShow(s)}
                        className={`flex shrink-0 items-center gap-1.5 px-3 py-1.5 font-body text-xs font-semibold ${s.is_public ? "bg-accent text-black" : "border border-border text-muted-foreground hover:text-white"}`}>
                  {s.is_public ? <><Eye className="h-3.5 w-3.5" /> Public</> : <><EyeOff className="h-3.5 w-3.5" /> Private</>}
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
